<template>
  <div class="animated fadeIn">
    <div class="container py-4">
      <!-- Progress Indicator -->
      <ProgressIndicator :current-step="currentStep" />
      
      <!-- Wizard Content -->
      <component
        :is="currentStepComponent"
        ref="currentStepRef"
        @update-back-button-label="handleUpdateBackButtonLabel"
        @update-back-button-disabled="handleUpdateBackButtonDisabled"
        @update-next-button-label="handleUpdateNextButtonLabel"
        @update-next-button-disabled="handleUpdateNextButtonDisabled"
      />
      
      <!-- Navigation -->
      <WizardNavigation
        :can-go-back="effectiveCanGoBack"
        :can-cancel="canCancel"
        :can-go-next="effectiveCanGoNext"
        :cancel-label="cancelLabel"
        :is-last-step="isLastStep"
        :show-save-draft="showSaveDraft"
        :is-saving-draft="isSavingDraft"
        :back-button-label="customBackButtonLabel"
        :next-button-label="customNextButtonLabel"
        @back="handleBack"
        @next="handleNext"
        @save-draft="handleSaveDraft"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ProgressIndicator from './ProgressIndicator.vue';
import WizardNavigation from './WizardNavigation.vue';
import Step1Identify from './Step1Identify.vue';
import Step2Overview from './Step2Overview.vue';
import Step3SelectQuestions from './Step3SelectQuestions.vue';
import Step4AnswerQuestions from './Step4AnswerQuestions.vue';
import Step5Review from './Step5Review.vue';

export default {
  name: 'InquiryWizard',
  
  components: {
    ProgressIndicator,
    WizardNavigation,
    Step1Identify,
    Step2Overview,
    Step3SelectQuestions,
    Step4AnswerQuestions,
    Step5Review,
  },

  data() {
    return {
      customBackButtonLabel: null,
      customBackButtonDisabled: null,
      customNextButtonLabel: null,
      customNextButtonDisabled: null,
      beforeUnloadHandler: null,
      draftJustSaved: false,
    };
  },
  
  computed: {
    ...mapGetters('inquiryWizard', [
      'currentStep',
      'canGoBack',
      'canGoNext',
      'cancelLabel',
      'canCancel',
      'isLastStep',
      'showSaveDraft',
      'isSavingDraft',
    ]),
    
    currentStepComponent() {
      const stepMap = {
        1: 'Step1Identify',
        2: 'Step2Overview',
        3: 'Step3SelectQuestions',
        4: 'Step4AnswerQuestions',
        5: 'Step5Review',
      };
      return stepMap[this.currentStep] || 'Step1Identify';
    },

    effectiveCanGoBack() {
      // If custom disabled state is set, use it; otherwise use Vuex state
      if (this.customBackButtonDisabled !== null && this.customBackButtonDisabled !== undefined) {
        return !this.customBackButtonDisabled;
      }
      return this.canGoBack;
    },

    effectiveCanGoNext() {
      // If custom disabled state is set, use it; otherwise use Vuex state
      if (this.customNextButtonDisabled !== null && this.customNextButtonDisabled !== undefined) {
        return !this.customNextButtonDisabled;
      }
      return this.canGoNext;
    },
  },

  watch: {
    currentStep() {
      // Reset custom button states when step changes
      this.customBackButtonLabel = null;
      this.customBackButtonDisabled = null;
      this.customNextButtonLabel = null;
      this.customNextButtonDisabled = null;
    },
  },
  
  methods: {
    ...mapActions('inquiryWizard', [
      'nextStep',
      'previousStep',
    ]),

    handleUpdateBackButtonLabel(label) {
      this.customBackButtonLabel = label;
    },

    handleUpdateBackButtonDisabled(disabled) {
      this.customBackButtonDisabled = disabled;
    },

    handleUpdateNextButtonLabel(label) {
      this.customNextButtonLabel = label;
    },

    handleUpdateNextButtonDisabled(disabled) {
      this.customNextButtonDisabled = disabled;
    },
    
    handleBack() {
      // Allow step component to intercept back navigation
      if (this.$refs.currentStepRef && typeof this.$refs.currentStepRef.handleWizardBackClick === 'function') {
        const shouldProceed = this.$refs.currentStepRef.handleWizardBackClick();
        if (!shouldProceed) {
          return; // Component handled navigation internally
        }
      }
      this.previousStep();
    },
    
    handleNext() {
      // Allow step component to intercept next navigation
      if (this.$refs.currentStepRef && typeof this.$refs.currentStepRef.handleWizardNextClick === 'function') {
        const shouldProceed = this.$refs.currentStepRef.handleWizardNextClick();
        if (!shouldProceed) {
          return; // Component handled navigation internally
        }
      }

      if (this.isLastStep) {
        this.submitInquiry();
      } else {
        this.nextStep();
      }
    },
    
    submitInquiry() {
      // No-op: actual submission is handled by Step5Review via handleWizardNextClick()
    },

    async handleSaveDraft() {
      try {
        await this.$store.dispatch('inquiryWizard/saveDraft');
        this.draftJustSaved = true;
        this.$bvToast.toast(this.$t('Webportal.Actions.InquiryWizard.Draft.SavedMessage'), {
          title: this.$t('Webportal.Actions.InquiryWizard.Draft.SavedTitle'),
          variant: 'success',
        });
        this.$router.push({ name: 'SurveyRevisionExplorer' });
      } catch (error) {
        this.$bvToast.toast(this.$t('Webportal.Actions.InquiryWizard.Draft.SaveError'), {
          title: this.$t('Webportal.Error'),
          variant: 'danger',
        });
      }
    },

    handleBeforeUnload(event) {
      if (this.$store.getters['inquiryWizard/selectedCheckId']) {
        event.preventDefault();
        event.returnValue = '';
        return '';
      }
    },
  },
  
  created() {
    // Initialize wizard at step 1
    if (this.currentStep === 0) {
      this.$store.dispatch('inquiryWizard/navigateToStep', 1);
    }

    // Setup beforeunload handler
    this.beforeUnloadHandler = this.handleBeforeUnload.bind(this);
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  },

  beforeDestroy() {
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    }
  },

  async beforeRouteLeave(to, from, next) {
    if (this.draftJustSaved) {
      next();
      return;
    }

    if (this.$store.getters['inquiryWizard/selectedCheckId']) {
      const answer = await this.$bvModal.msgBoxConfirm(
        this.$t('Webportal.Actions.InquiryWizard.UnsavedChanges.Text'),
        {
          title: this.$t('Webportal.Actions.InquiryWizard.UnsavedChanges.Title'),
          okTitle: this.$t('Webportal.Actions.InquiryWizard.UnsavedChanges.Ok'),
          cancelTitle: this.$t('Webportal.Cancel'),
          okVariant: 'warning',
          hideHeaderClose: false,
        }
      );

      if (answer) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
};
</script>

<style lang="scss" scoped>
// Minimal custom styles - most styling comes from Bootstrap
</style>
